/**
 * Pixelfed platform adapter — created via the ActivityPub adapter factory.
 * Wraps pixelfedService functions and passes them into createActivityPubAdapter.
 */
import type { PixelfedSession } from '@/lib/pixelfed'
import { lookupAccount, getRelationship, followAccount, unfollowAccount } from '@/lib/pixelfed'
import {
    fetchPixelfedTimeline,
    fetchPixelfedProfilePosts,
    favouritePixelfedStatus,
    unfavouritePixelfedStatus,
    reblogPixelfedStatus,
    unreblogPixelfedStatus,
    deletePixelfedStatus,
    fetchPixelfedFavourites,
    bookmarkPixelfedStatus,
    unbookmarkPixelfedStatus,
    fetchPixelfedBookmarks,
} from '@/services/pixelfedService'
import { fetchPixelfedHashtagPosts } from '@/services/hashtagService'
import { searchPixelfedUsers, searchPixelfedPosts } from '@/services/searchService'
import {
    fetchMastodonNotifications,
    fetchMastodonUnreadCount,
} from '@/services/notificationsService'
import { createActivityPubAdapter } from './activityPubAdapter'
import type { ActivityPubSession } from '@/lib/mastodon'

export const pixelfedAdapter = createActivityPubAdapter({
    platform: 'pixelfed',
    timelinePrefix: 'pixelfed',
    supportsListTimeline: false,
    supportsHashtagTimeline: false,

    capabilities: {
        reply: true,
        like: true,
        repost: true,
        quote: false,
        delete: true,
        follow: true,
        feeds: false,
        hashtags: true,
        gridView: true,
        likedPostsFeed: false,
        favouritesFeed: true,
        profileRepliesTab: false,
        bookmarks: true,
        // Pixelfed's pin endpoints are unverified — out of v1 scope
        pinPosts: false,
        lists: false,
    },

    services: {
        fetchTimeline: (session, options) =>
            fetchPixelfedTimeline(session as PixelfedSession, options),

        favouriteStatus: (session, id) => favouritePixelfedStatus(session as PixelfedSession, id),

        unfavouriteStatus: (session, id) =>
            unfavouritePixelfedStatus(session as PixelfedSession, id),

        reblogStatus: (session, id) => reblogPixelfedStatus(session as PixelfedSession, id),

        unreblogStatus: (session, id) => unreblogPixelfedStatus(session as PixelfedSession, id),

        deleteStatus: (session, id) => deletePixelfedStatus(session as PixelfedSession, id),

        fetchFavourites: (session, options) =>
            fetchPixelfedFavourites(session as PixelfedSession, options),

        bookmarkStatus: (session, id) => bookmarkPixelfedStatus(session as PixelfedSession, id),

        unbookmarkStatus: (session, id) => unbookmarkPixelfedStatus(session as PixelfedSession, id),

        fetchBookmarks: (session, options) =>
            fetchPixelfedBookmarks(session as PixelfedSession, options),

        searchUsers: (query, session) => searchPixelfedUsers(query, session as PixelfedSession),

        searchPosts: (query, session, offset) =>
            searchPixelfedPosts(query, session as PixelfedSession, offset),

        fetchNotifications: (session, options, platform) =>
            fetchMastodonNotifications(session as PixelfedSession, options, platform),

        fetchUnreadCount: session => fetchMastodonUnreadCount(session as PixelfedSession),

        fetchProfilePosts: (identifier, options) =>
            fetchPixelfedProfilePosts(identifier, {
                cursor: options.cursor,
                pixelfedSession: options.session as PixelfedSession,
            }),

        fetchHashtagPosts: (tag, session, cursor) =>
            fetchPixelfedHashtagPosts(tag, session as PixelfedSession, cursor),

        lookupAccount: (session: ActivityPubSession, identifier: string) =>
            lookupAccount(session as PixelfedSession, identifier),

        getRelationship: (session: ActivityPubSession, id: string) =>
            getRelationship(session as PixelfedSession, id),

        followAccount: (session: ActivityPubSession, id: string) =>
            followAccount(session as PixelfedSession, id),

        unfollowAccount: (session: ActivityPubSession, id: string) =>
            unfollowAccount(session as PixelfedSession, id),

        // Pixelfed does not implement block operations
    },
})
